import { CardDetails } from "@/models/CardDetails";
import axios, { AxiosInstance } from "axios";
import logger, { LoggingSource } from "@/shared/Logger";
import IPaymentTokenizeApi from "./IPaymentTokenizeApi";
import PaymentSource, {
  AdministrationPaymentSource,
} from "@/models/APIModels/PaymentSource";
import BankDetails from "@/models/BankDetails";
import { getApiClient } from "./ApiClient/ApiClient";
import store from "@/store";
import SystemSettingApi from "./SystemSettingApi";
import { PAYMENTPROVIDER_HEADER } from "@/shared/constants";

import { createApiException, ApiException } from "./ResponseModel";
import { PaymentSourceTypeEnum } from "@/shared/ModelEnums";

export default class PayChoiceTokenizeApi implements IPaymentTokenizeApi {
  private readonly _axiosInstance: AxiosInstance;
  private readonly _bankTokenizeController: string;

  constructor(userCategory: string) {
    this._bankTokenizeController =
      userCategory === "UC_D" ? "/donor" : "/committeemember";
    const baseURL =
      userCategory === "UC_D"
        ? store.state.systemSetting?.donationsPaymentProviderAPIURL
        : store.state.systemSetting?.administrationPayerPaymentProviderAPIURL;

    this._axiosInstance = axios.create({
      headers: PAYMENTPROVIDER_HEADER,

      baseURL,
    });
  }

  async tokenizeCard(cardDetails: CardDetails) {
    try {
      logger.info("tokenizeCard...", LoggingSource.UIDonorDonationDetails);

      logger.timeStart(
        "tokenizeCard call",
        LoggingSource.UIDonorDonationDetails,
      );

      const data = {
        card: {
          name: cardDetails.cardholderName,
          number: cardDetails.cardNumber,
          cvv: cardDetails.ccv,
          expiry_month: cardDetails.expiryMonth,
          expiry_year: cardDetails.expiryYear % 2000,
        },
      };
      const systemSettingAPI = new SystemSettingApi();
      const responseForKey = await systemSettingAPI.getPublicKey();
      if (!responseForKey.content)
        throw createApiException(
          "Public key not found",
          "Public Key was not found in the response",
        );
      const PUBLIC_KEY = responseForKey.content;

      const resultData: {
        data: {
          card: {
            token: string;
            card_name: string;
            card_type: string;
            expiry_month: number;
            expiry_year: number;
            masked_number: string;
          };
          error?: { message: string; param: string };
        };
      } = await this._axiosInstance.post(
        `/api/v3/token?key=${PUBLIC_KEY}`,
        data,
      );

      if (resultData.data.error)
        throw createApiException(
          resultData.data.error.message,
          resultData.data.error.param,
        );

      const card = resultData.data.card;
      const result: AdministrationPaymentSource = {
        paymentProviderToken: card.token,
        name: card.card_name,
        expiryMonth: card.expiry_month + "",
        expiryYear: card.expiry_year + "",
        type: card.card_type,
        numberMask: card.masked_number,
        paymentSourceTypeCd: PaymentSourceTypeEnum.PST_CC,
        fromDT: new Date(),
      };
      logger.timeEnd("tokenizeCard call", LoggingSource.UIDonorDonationDetails);

      logger.debug(
        `tokenizeCard response: ${JSON.stringify(resultData)}`,
        LoggingSource.UIDonorDonationDetails,
      );

      return result;
    } catch (error) {
      logger.timeEnd("tokenizeCard call", LoggingSource.UIDonorDonationDetails);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorDonationDetails);
      if ((error as ApiException).title) {
        throw error;
      } else
        throw createApiException(
          (error as Error).message,
          "Could not tokenize card",
        );
    } finally {
      logger.info("...tokenizeCard", LoggingSource.UIDonorDonationDetails);
    }
  }

  async tokenizeBankDetails(
    bankDetails: BankDetails,
  ): Promise<AdministrationPaymentSource> {
    try {
      logger.info("tokenizeBank...", LoggingSource.UIDonorDonationDetails);

      logger.timeStart(
        "tokenizeBank call",
        LoggingSource.UIDonorDonationDetails,
      );

      const bankPaymentSource: PaymentSource = {
        name: bankDetails.bankAccountName,
        bsb: bankDetails.bankAccountBSB,
        number: bankDetails.bankAccountNumber,
        paymentSourceTypeCd: PaymentSourceTypeEnum.PST_BA,
        paymentProviderToken: "",
      };

      const client = getApiClient();

      const resultData = await client.post<PaymentSource, PaymentSource>(
        `${this._bankTokenizeController}/tokenizeBankAccount`,
        bankPaymentSource,
      );

      logger.timeEnd("tokenizeBank call", LoggingSource.UIDonorDonationDetails);

      logger.debug(
        `tokenizeBank response: ${JSON.stringify(resultData)}`,
        LoggingSource.UIDonorDonationDetails,
      );

      return { ...resultData.content!, fromDT: new Date() }; // TODO: check if error occurs here and throw the content message
    } catch (error) {
      logger.timeEnd("tokenizeBank call", LoggingSource.UIDonorDonationDetails);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorDonationDetails);
      throw error;
    } finally {
      logger.info("...tokenizeBank", LoggingSource.UIDonorDonationDetails);
    }
  }
}
